/**
 * Morse — „Sprechen“: die Antwort einsprechen statt tippen. (08.10.2026, Reiter Hören)
 *
 * Martin: „Super wäre, wenn ich statt Buchstaben zu drücken Buchstaben aussprechen könnte, während des Trainings, wenn
 * die Pieptöne reinkommen, als zusätzliche Möglichkeit, Buchstaben einzugeben.“ — „Beides wäre gut“ (am Stück UND
 * Buchstabe für Buchstabe: jedes Antippen ist eine kurze Aufnahme, was erkannt wird, wächst im Feld).
 *
 * Diese Klasse ist der Knopf mit seinem Ablauf — nicht das Mikrofon. Das Mikrofon und die Mitschrift stellt der
 * `DiktatAnschluss` (morse-sprechen-diktat.ts: das vorhandene Live-Diktat, KEIN zweiter Aufnahme-Motor); die Zeichen zieht
 * der Leser (morse-sprechen-leser.ts). Der Hören-Reiter sagt nur, WANN der Knopf zählt (`zeige`) und was ins Feld darf.
 *
 * Die Regeln:
 *   - Nur nach dem Ton (`zeige("tippt")`): das Mikrofon würde die Töne mithören, und iOS lässt Wiedergabe und Aufnahme nicht
 *     zugleich auf einer Tonsitzung zu. Der Knopf ist vorher da, aber aus — mit einem Satz, warum.
 *   - Im Anruf, bei laufender Sprechtaste und bei laufender Wiedergabe geht es nicht (`DiktatAnschluss.sperre`).
 *   - Ende: erneutes Antippen, Stille nach dem Gesprochenen (`STILLE_MS`), das Satzende der Erkennung, oder nach
 *     `HOECHSTENS_MS`. Wird nach `KEIN_TEXT_MS` nichts gehört, endet es mit einem Satz.
 *   - Das Ergebnis geht NIE von selbst ab: es steht im Feld, der Hören-Reiter lässt Zeit zum Prüfen. Unsichere Zeichen
 *     bleiben leer (`LUECKE`) und haben darunter ihre Möglichkeiten zum Antippen.
 *   - Jeder Fehler hat einen deutschen Satz in der Statuszeile. Nichts bleibt stillschweigend leer.
 *   - Datenschutz: kein Audio wird gespeichert, die Mitschrift lebt nur im Speicher (und im Feld). Die Diagnosezeile
 *     (`spur`) hat Zahlen und Zustände, nie Gehörtes.
 *
 * ⚠️ Der Tipp auf „Sprechen“ ist die Geste, in der iOS den AudioContext des Diktats freigibt: `anschluss.starte` läuft darum
 *    im Klick OHNE vorheriges `await` (live-dictation.ts, `start`).
 */
import { SPRECHEN } from "./morse-texte.js";
import { baueMeldung, el, knopf, meldeIn, type MeldungsArt } from "./morse-teile.js";
import { LUECKE, leseGesprochen, type SprachWort } from "./morse-sprechen-leser.js";
import { mikrofonZeichen } from "./mikro-zeichen.js";

/** Nach so viel Stille, nachdem etwas gesagt wurde, endet die Aufnahme. */
export const STILLE_MS = 1300;
/** Meldet die Erkennung ein Satzende, endet sie schon nach so viel (die letzten Wörter brauchen einen Moment). */
export const SATZENDE_MS = 450;
/** Länger als das nimmt eine Aufnahme nie auf. */
export const HOECHSTENS_MS = 8000;
/** Kommt bis dahin kein einziges Wort an, endet sie mit „nichts gehört“. */
export const KEIN_TEXT_MS = 5000;

/** Warum ein Diktat nicht ging. */
export type DiktatFehlerArt = "recht" | "kein-mikro" | "netz" | "sonst";

/** Der Fehler, mit dem `DiktatLauf.bereit` scheitert. */
export class DiktatFehler extends Error {
  constructor(readonly art: DiktatFehlerArt) {
    super(`diktat-${art}`);
    this.name = "DiktatFehler";
  }
}

export interface DiktatErgebnis {
  /** Der ganze Text, wie die Erkennung ihn schreibt. */
  text: string;
  /** Dieselben Wörter mit Konfidenz, wenn die Erkennung sie liefert. */
  woerter?: SprachWort[];
}

/** Eine laufende kurze Aufnahme mit Mitschrift. */
export interface DiktatLauf {
  /** Die Leitung steht und das Mikrofon hört. Scheitert mit `DiktatFehler` (kein Recht, kein Mikrofon …). */
  bereit: Promise<void>;
  /** Aufnahme beenden und auf den Rest der Mitschrift warten — höchstens `warteMs` (fehlt es: die Vorgabe des Diktats). */
  beende(warteMs?: number): Promise<DiktatErgebnis>;
  /** Aufnahme beenden und alles verwerfen. */
  verwirf(): void;
}

export interface DiktatRueckrufe {
  /**
   * Der Text bis jetzt (bestätigt + vorläufig) — und dieselben Wörter mit Konfidenz, wenn die Erkennung sie liefert.
   * `fest`: alles davon ist schon bestätigt (nichts Vorläufiges mehr) — der Abschluss der Erkennung ändert es nicht mehr.
   */
  beiText(ganz: string, woerter?: readonly SprachWort[], fest?: boolean): void;
  /** Die Erkennung meldet ein Satzende. */
  beiSatzende(): void;
  /** Unterwegs ging etwas schief. */
  beiFehler(art: DiktatFehlerArt): void;
}

/** Was der Knopf vom Gerät braucht (in der App morse-sprechen-diktat.ts, im Test eine Attrappe). */
export interface DiktatAnschluss {
  /** Warum JETZT nicht aufgenommen werden darf (Anruf, Sprechtaste, Wiedergabe) — oder `null`. */
  sperre(): string | null;
  /** Aufnahme beginnen — synchron, in der Geste des Tipps. */
  starte(rueckrufe: DiktatRueckrufe): DiktatLauf;
}

/** Was der Knopf vom Hören-Reiter braucht. */
export interface SprechKontext {
  /** Wie viele Zeichen noch in das Feld passen (Länge der Gruppe minus das, was schon dort steht). */
  platz(): number;
  /** Die Zeichen der Runde (für Verwechslungen im Leser). */
  erlaubt(): ReadonlySet<string>;
  /** Was im Feld steht. */
  feld(): string;
  /** Das Feld setzen (ohne es abzuschicken). */
  setzeFeld(text: string): void;
  /** Das Mikrofon beginnt: Uhr und Frist ruhen, der Ton darf schlafen. */
  beiStart(): void;
  /** Die Aufnahme ist vorbei (ob etwas im Feld steht, ob Lücken offen sind) — oder ein Zeichen wurde angetippt. */
  beiEnde(o: { gefuellt: boolean; offen: number }): void;
  /** Diagnosezeile — Zahlen und Zustände, nie Inhalt. */
  spur(text: string): void;
}

type Zustand = "ruht" | "nimmt-auf" | "versteht";
/** wartet: die Gruppe klingt noch · tippt: jetzt darf gesprochen werden · aus: gerade keine Antwort fällig. */
type SprechPhase = "wartet" | "tippt" | "aus";

/** Eine Stelle im Feld, die leer blieb, mit dem, was dort stehen könnte. */
interface OffeneStelle {
  stelle: number;
  moegliche: string[];
}

export class MorseSprechen {
  readonly wurzel: HTMLElement;
  private readonly knopfEl: HTMLButtonElement;
  private readonly knopfText: HTMLElement;
  private readonly meldung: HTMLElement;
  private readonly wahl: HTMLElement;
  private zustand: Zustand = "ruht";
  private phase: SprechPhase = "aus";
  private lauf: DiktatLauf | null = null;
  /** Zählt jede Aufnahme und jedes Abbrechen: eine späte Antwort einer alten Aufnahme gilt nicht mehr. */
  private nr = 0;
  private text = "";
  private begonnen = 0;
  private offen: OffeneStelle[] = [];
  /** Der Satz zur Lage, den WIR gesetzt haben (nicht ein Ergebnis) — nur den nehmen wir wieder weg. */
  private lageText = "";
  private uhrStille: ReturnType<typeof setTimeout> | null = null;
  private uhrHoechstens: ReturnType<typeof setTimeout> | null = null;
  private uhrKeinText: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly anschluss: DiktatAnschluss,
    private readonly ctx: SprechKontext,
    private readonly jetzt: () => number = () => performance.now(),
  ) {
    this.wurzel = el("div", "morse-sprech");
    this.knopfEl = el("button", "morse-knopf morse-sprech-knopf");
    this.knopfEl.type = "button";
    this.knopfText = el("span", "morse-sprech-text", SPRECHEN.knopf);
    this.knopfEl.append(mikrofonZeichen(18), this.knopfText);
    // Der Tipp darf dem Feld den Fokus nicht nehmen — sonst schließt das iPhone die Tastatur (wie „Gruppe fertig“).
    this.knopfEl.addEventListener("pointerdown", (ev) => ev.preventDefault());
    this.knopfEl.addEventListener("click", () => this.klick());
    this.meldung = baueMeldung();
    this.wahl = el("div", "morse-sprech-wahl");
    this.wahl.setAttribute("role", "group");
    this.wahl.setAttribute("aria-label", SPRECHEN.wahl.gruppe);
    this.wahl.hidden = true;
    this.wurzel.append(this.knopfEl, this.meldung, this.wahl);
    this.zeichne();
  }

  /** Läuft gerade eine Aufnahme (oder die Auswertung)? */
  get beschaeftigt(): boolean {
    return this.zustand !== "ruht";
  }

  /* ── Wann der Knopf zählt ───────────────────────────────────────── */

  /** Der Hören-Reiter meldet seine Phase: nur nach dem Ton (`tippt`) darf gesprochen werden. */
  zeige(phase: SprechPhase): void {
    // Eine neue Gruppe klingt, oder die Antwort ist gewertet: was zur letzten Gruppe gesagt wurde, gilt nicht mehr.
    if (phase !== "tippt" && this.phase !== phase) this.zuruecksetzen();
    this.phase = phase;
    // Wechselt die Phase mitten in einer Aufnahme (Gruppe ausgewertet, Pause), gilt sie nicht mehr.
    if (phase !== "tippt" && this.zustand !== "ruht") this.abbrechen();
    this.zeichne();
  }

  /** Noch ein Zeichen mehr im Feld, das nicht von uns kommt: die Markierungen stimmen nicht mehr. */
  feldGeaendert(): void {
    if (this.zustand === "nimmt-auf") this.abbrechen();
    if (!this.offen.length && this.wahl.hidden) return;
    this.offen = [];
    this.zeichneWahl();
  }

  /** Neue Gruppe oder Ende: Markierungen und Satz weg. */
  zuruecksetzen(): void {
    this.offen = [];
    this.zeichneWahl();
    meldeIn(this.meldung, "", "info");
  }

  /** Alles aus, ohne etwas auszuwerten. Gibt `true` zurück, wenn gerade aufgenommen wurde. */
  abbrechen(): boolean {
    const war = this.zustand !== "ruht";
    this.nr++;
    this.uhrenAus();
    const lauf = this.lauf;
    this.lauf = null;
    this.zustand = "ruht";
    if (lauf) {
      try {
        lauf.verwirf();
      } catch {
        /* schon zu */
      }
    }
    if (war) {
      meldeIn(this.meldung, "", "info");
      this.zeichne();
    }
    return war;
  }

  zerstoere(): void {
    this.abbrechen();
  }

  /* ── Der Ablauf ─────────────────────────────────────────────────── */

  private klick(): void {
    if (this.zustand === "nimmt-auf") {
      this.beende("antippen");
      return;
    }
    if (this.zustand !== "ruht" || this.phase !== "tippt") return;
    const sperre = this.anschluss.sperre();
    if (sperre) {
      this.melde(sperre, "hinweis");
      this.ctx.spur("sprechen start=gesperrt");
      return;
    }
    if (this.ctx.platz() <= 0) {
      this.melde(SPRECHEN.fehler.voll, "hinweis");
      return;
    }
    this.beginne();
  }

  private beginne(): void {
    const nr = ++this.nr;
    this.text = "";
    this.begonnen = this.jetzt();
    this.ctx.beiStart();
    this.zustand = "nimmt-auf";
    this.melde(SPRECHEN.hoert, "info");
    this.zeichne();
    let lauf: DiktatLauf;
    try {
      lauf = this.anschluss.starte({
        beiText: (ganz) => this.beiText(nr, ganz),
        beiSatzende: () => this.beiSatzende(nr),
        beiFehler: (art) => this.fehlerEnde(nr, art),
      });
    } catch {
      this.fehlerEnde(nr, "sonst");
      return;
    }
    this.lauf = lauf;
    lauf.bereit.catch((e: unknown) => this.fehlerEnde(nr, e instanceof DiktatFehler ? e.art : "sonst"));
    this.uhrHoechstens = setTimeout(() => this.beende("hoechstens", nr), HOECHSTENS_MS);
    this.uhrKeinText = setTimeout(() => {
      if (!this.text.trim()) this.beende("kein-text", nr);
    }, KEIN_TEXT_MS);
    this.ctx.spur("sprechen start=ok");
  }

  private beiText(nr: number, ganz: string): void {
    if (nr !== this.nr || this.zustand !== "nimmt-auf") return;
    this.text = ganz;
    // Etwas gesagt: ab jetzt endet es nach einer Pause.
    if (ganz.trim()) this.armStille(STILLE_MS, nr);
  }

  private beiSatzende(nr: number): void {
    if (nr !== this.nr || this.zustand !== "nimmt-auf" || !this.text.trim()) return;
    this.armStille(SATZENDE_MS, nr);
  }

  private armStille(ms: number, nr: number): void {
    if (this.uhrStille) clearTimeout(this.uhrStille);
    this.uhrStille = setTimeout(() => this.beende("stille", nr), ms);
  }

  private beende(grund: string, nr: number = this.nr): void {
    if (nr !== this.nr || this.zustand !== "nimmt-auf" || !this.lauf) return;
    this.uhrenAus();
    this.zustand = "versteht";
    this.melde(SPRECHEN.versteht, "info");
    this.zeichne();
    const lauf = this.lauf;
    const ms = Math.round(this.jetzt() - this.begonnen);
    lauf.beende().then(
      (e) => {
        if (nr !== this.nr) return;
        this.auswerten(e, grund, ms);
      },
      () => this.fehlerEnde(nr, "sonst"),
    );
  }

  private auswerten(e: DiktatErgebnis, grund: string, ms: number): void {
    this.lauf = null;
    this.zustand = "ruht";
    // Gezeigt wird, was gelesen wurde: haben wir die Wörter mit Konfidenz, sind sie die Quelle (sie enthalten auch den Rest).
    const mitWoertern = !!e.woerter?.length;
    const gehoert = (mitWoertern ? e.woerter!.map((w) => w.text).join(" ") : e.text).trim();
    const platz = this.ctx.platz();
    const g = leseGesprochen(mitWoertern ? e.woerter! : gehoert, { laenge: platz, erlaubt: this.ctx.erlaubt() });
    this.ctx.spur(
      `sprechen ende=${grund} ms=${ms} woerter=${gehoert ? gehoert.split(/\s+/).length : 0} zeichen=${g.zeichen.length} luecken=${g.luecken} zuviel=${g.abgeschnitten} unverstanden=${g.unverstanden.length}`,
    );
    if (!gehoert) {
      this.melde(SPRECHEN.fehler.nichts, "fehler");
      this.beendet(false);
      return;
    }
    if (g.leer) {
      this.melde(SPRECHEN.fehler.nichtsVerstanden(gehoert), "fehler");
      this.beendet(false);
      return;
    }
    // Das Gesprochene WÄCHST an das, was schon im Feld steht (Buchstabe für Buchstabe) — es ersetzt nichts.
    const vorher = this.ctx.feld().replace(/\s+/g, "");
    const stelleAb = [...vorher].length;
    g.zeichen.forEach((z, i) => {
      if (z.zeichen === null) this.offen.push({ stelle: stelleAb + i + 1, moegliche: z.moegliche });
    });
    this.ctx.setzeFeld(vorher + g.feld);
    const saetze = [SPRECHEN.ergebnis.gehoert(gehoert)];
    if (g.abgeschnitten) saetze.push(SPRECHEN.ergebnis.zuviel(g.abgeschnitten, stelleAb + g.zeichen.length));
    if (g.unverstanden.length) saetze.push(SPRECHEN.ergebnis.unverstanden(g.unverstanden.map((w) => `„${w}“`).join(", ")));
    saetze.push(this.offen.length ? SPRECHEN.ergebnis.luecken(this.offen.length) : SPRECHEN.ergebnis.pruefen);
    this.melde(saetze.join(" "), this.offen.length || g.abgeschnitten ? "hinweis" : "info");
    this.zeichneWahl();
    this.beendet(true);
  }

  private fehlerEnde(nr: number, art: DiktatFehlerArt): void {
    if (nr !== this.nr || this.zustand === "ruht") return;
    this.uhrenAus();
    const lauf = this.lauf;
    this.lauf = null;
    this.zustand = "ruht";
    try {
      lauf?.verwirf();
    } catch {
      /* schon zu */
    }
    const satz = art === "recht" ? SPRECHEN.fehler.recht : art === "kein-mikro" ? SPRECHEN.fehler.keinMikro : art === "netz" ? SPRECHEN.fehler.netz : SPRECHEN.fehler.sonst;
    this.ctx.spur(`sprechen ende=fehler art=${art} ms=${Math.round(this.jetzt() - this.begonnen)}`);
    this.melde(satz, "fehler");
    this.beendet(false);
  }

  private beendet(gefuellt: boolean): void {
    this.zeichne();
    this.ctx.beiEnde({ gefuellt, offen: this.offen.length });
  }

  /** Ein Zeichen aus der Auswahl unter dem Feld in die leere Stelle setzen. */
  private waehle(o: OffeneStelle, z: string): void {
    const i = this.offen.indexOf(o);
    if (i < 0) return;
    const zeichen = [...this.ctx.feld().replace(/\s+/g, "")];
    // Die Stelle steht fest, solange nichts getippt wurde (sonst sind die Markierungen schon weg); sicherheitshalber: die i-te Lücke.
    let pos = zeichen[o.stelle - 1] === LUECKE ? o.stelle - 1 : -1;
    if (pos < 0) {
      let n = -1;
      pos = zeichen.findIndex((c) => c === LUECKE && ++n === i);
    }
    if (pos < 0) return;
    zeichen[pos] = z;
    this.offen.splice(i, 1);
    this.ctx.setzeFeld(zeichen.join(""));
    this.zeichneWahl();
    this.ctx.spur(`sprechen wahl offen=${this.offen.length}`);
    this.ctx.beiEnde({ gefuellt: true, offen: this.offen.length });
  }

  /* ── Anzeige ────────────────────────────────────────────────────── */

  private melde(text: string, art: MeldungsArt): void {
    meldeIn(this.meldung, text, art);
  }

  private uhrenAus(): void {
    for (const u of [this.uhrStille, this.uhrHoechstens, this.uhrKeinText]) if (u) clearTimeout(u);
    this.uhrStille = this.uhrHoechstens = this.uhrKeinText = null;
  }

  private zeichne(): void {
    const z = this.zustand;
    const sperre = this.phase === "tippt" && z === "ruht" ? this.anschluss.sperre() : null;
    const an = this.phase === "tippt" && (z === "nimmt-auf" || (z === "ruht" && !sperre));
    this.knopfEl.disabled = !an;
    this.knopfEl.dataset.zustand = z;
    this.knopfText.textContent = z === "nimmt-auf" ? SPRECHEN.fertig : z === "ruht" && this.phase === "wartet" ? SPRECHEN.knopfWartet : SPRECHEN.knopf;
    this.knopfEl.setAttribute(
      "aria-label",
      z === "nimmt-auf" ? SPRECHEN.aria.nimmtAuf : z === "versteht" ? SPRECHEN.aria.versteht : this.phase !== "tippt" ? SPRECHEN.aria.wartet : sperre ? SPRECHEN.aria.gesperrt : SPRECHEN.aria.bereit,
    );
    // Der Satz zur Lage (Ton läuft noch / gesperrt) — ein Ergebnis oder ein Fehler bleibt stehen, bis er ersetzt wird.
    const lage = z !== "ruht" ? "" : this.phase === "wartet" ? SPRECHEN.nachDemTon : (sperre ?? "");
    // Nur unseren eigenen Lage-Satz ersetzen oder wegnehmen — ein Ergebnis oder ein Fehler bleibt stehen.
    const unser = this.lageText !== "" && !this.meldung.hidden && this.meldung.textContent === this.lageText;
    if (lage && (this.meldung.hidden || unser)) {
      this.lageText = lage;
      this.melde(lage, this.phase === "wartet" ? "info" : "hinweis");
    } else if (!lage) {
      if (unser) meldeIn(this.meldung, "", "info");
      this.lageText = "";
    }
  }

  private zeichneWahl(): void {
    this.wahl.textContent = "";
    this.wahl.hidden = this.offen.length === 0;
    for (const o of this.offen) {
      const reihe = el("div", "morse-sprech-wahl-reihe");
      reihe.append(el("span", "morse-sprech-wahl-stelle", SPRECHEN.wahl.stelle(o.stelle)));
      for (const z of o.moegliche) {
        const b = knopf(z, "morse-knopf morse-sprech-wahl-knopf", () => this.waehle(o, z));
        b.setAttribute("aria-label", SPRECHEN.wahl.aria(o.stelle, z));
        b.addEventListener("pointerdown", (ev) => ev.preventDefault());
        reihe.appendChild(b);
      }
      this.wahl.appendChild(reihe);
    }
  }
}
